import { del, get, post, put, upload } from '@api/request';

export async function fetchAbstractSubdivisions(
  { appraisalService },
  authenticationToken,
  { asCode, description, page, pageSize, tag, sortDirection, sortField },
) {
  let url = `${appraisalService}/appraisal/abstractsubdivisions?asCode=${asCode}&description=${description}&page=${page}&pageSize=${pageSize}`;
  if (tag) {
    url += `&tag=${tag}`;
  }
  if (sortDirection && sortField) {
    url += `&sortDirection=${sortDirection}&sortField=${sortField}`;
  }
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updateAbstractSubdivisionDescription(
  { appraisalService },
  authenticationToken,
  { abstractSubdivision, payload },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${abstractSubdivision}/description`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function insertAbstractSubdivision(
  { appraisalService },
  authenticationToken,
  { payload },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivisions`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function fetchAbstractSubdivisionIndicators(
  { appraisalService },
  authenticationToken,
) {
  const url = `${appraisalService}/appraisal/abstractsubdivisionindicators`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchAbstractSubdivision(
  { appraisalService },
  authenticationToken,
  { asCode },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updateAbstractSubdivision(
  { appraisalService },
  authenticationToken,
  { asCode, payload },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function deleteAbstractSubdivision(
  { appraisalService },
  authenticationToken,
  { asCode },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}`;
  const { body } = await del(authenticationToken, url);
  return body;
}

export async function fetchAbstractSubdivisionPlats(
  { appraisalService },
  authenticationToken,
  { asCode },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}/plats`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export const insertAbstractSubdivisionFile = async (
  { appraisalService },
  authenticationToken,
  { asCode, payload },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}/files`;
  const { body } = await upload(authenticationToken, url, payload);
  return body;
};

export const deleteAbstractSubdivisionFile = async (
  { appraisalService },
  authenticationToken,
  { asCode, s3ID, payload },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}/file/${s3ID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const downloadAbstractSubdivisionFile = async (
  { appraisalService },
  authenticationToken,
  { asCode, s3ID },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}/file/${s3ID}`;

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = s3ID;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};

export const updateAbstractSubdivisionFile = async (
  { appraisalService },
  authenticationToken,
  { asCode, payload },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}/files`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export async function fetchAbstractSubdivisionDetails(
  { appraisalService },
  authenticationToken,
  { asCode },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}/details`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function deleteAbstractSubdivisionDetail(
  { appraisalService },
  authenticationToken,
  { asCode, year },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivisiondetail/${asCode}/${year}`;
  const { body } = await del(authenticationToken, url);
  return body;
}

export async function insertAbstractSubdivisionDetail(
  { appraisalService },
  authenticationToken,
  { asCode, payload },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}/details`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function fetchAbstractSubdivisionDetail(
  { appraisalService },
  authenticationToken,
  { asCode, year },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivisiondetail/${asCode}/${year}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updateAbstractSubdivisionDetail(
  { appraisalService },
  authenticationToken,
  { asCode, year, payload },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivisiondetail/${asCode}/${year}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function fetchAbstractSubdivisionPlatTypes(
  { appraisalService },
  authenticationToken,
) {
  const url = `${appraisalService}/appraisal/codes?type=Abstract Subdivision&name=Plat Type`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function insertAbstractSubdivisionPlat(
  { appraisalService },
  authenticationToken,
  { asCode, payload },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}/plats`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export const insertAbstractSubdivisionDetailFile = async (
  { appraisalService },
  authenticationToken,
  { asCode, year, payload },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivisiondetail/${asCode}/${year}/files`;
  const { body } = await upload(authenticationToken, url, payload);
  return body;
};

export const deleteAbstractSubdivisionDetailFile = async (
  { appraisalService },
  authenticationToken,
  { asCode, year, s3ID, payload },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivisiondetail/${asCode}/${year}/file/${s3ID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const downloadAbstractSubdivisionDetailFile = async (
  { appraisalService },
  authenticationToken,
  { asCode, year, s3ID },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivisiondetail/${asCode}/${year}/file/${s3ID}`;

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = s3ID;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};

export const updateAbstractSubdivisionDetailFile = async (
  { appraisalService },
  authenticationToken,
  { asCode, year, payload },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivisiondetail/${asCode}/${year}/files`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export async function fetchAbstractSubdivisionPlat(
  { appraisalService },
  authenticationToken,
  { platID },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivisionplat/${platID}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updateAbstractSubdivisionPlat(
  { appraisalService },
  authenticationToken,
  { platID, payload },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivisionplat/${platID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export const insertAbstractSubdivisionPlatFile = async (
  { appraisalService },
  authenticationToken,
  { platID, payload },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivisionplat/${platID}/files`;
  const { body } = await upload(authenticationToken, url, payload);
  return body;
};

export const deleteAbstractSubdivisionPlatFile = async (
  { appraisalService },
  authenticationToken,
  { platID, s3ID, payload },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivisionplat/${platID}/file/${s3ID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const downloadAbstractSubdivisionPlatFile = async (
  { appraisalService },
  authenticationToken,
  { platID, s3ID },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivisionplat/${platID}/file/${s3ID}`;

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = s3ID;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};

export const updateAbstractSubdivisionPlatFile = async (
  { appraisalService },
  authenticationToken,
  { platID, payload },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivisionplat/${platID}/files`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const updateAbstractSubdivisionTags = async (
  { appraisalService },
  authenticationToken,
  { asCode, payload },
) => {
  const url = `${appraisalService}/appraisal/abstractsubdivision/${asCode}/tags`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export async function deleteAbstractSubdivisionPlat(
  { appraisalService },
  authenticationToken,
  { platID },
) {
  const url = `${appraisalService}/appraisal/abstractsubdivisionplat/${platID}`;
  const { body } = await del(authenticationToken, url);
  return body;
}